import { useEffect, useMemo, useState } from 'react';
import Heading from 'components/design-system/Heading/Heading';
import InputComponent from 'components/design-system/input/Input';
import LabelComponent from 'components/design-system/Label/Label';
import Text from 'components/design-system/Text/Text';
import {
  handleKeyPress,
  handlePaste
} from 'views/Business/utils/handleKeyPress';
import { DICTONARY } from 'const/Dictonary';
import { CircularProgress } from '@mui/material';
import ButtonComponent from 'components/design-system/Button/Button';
import Add from '@mui/icons-material/Add';
import {
  obtenerEmpresaGeneradora,
  obtenerEmpresasGeneradoras
} from 'views/CargaMasiva/hooks/useServices';
import { AutoCompleteAsyncController } from 'components/design-system/AutoCompleteAsync/AutoCompleteAsyncController';
import { isValorValido } from 'helpers/validaCampos';
import { SelectorDireccionContainer } from 'hooks/Selectors/hooks';

const Generador = ({
  errors,
  handleChange,
  isSubmitting,
  setFieldTouched,
  setFieldValue,
  touched,
  values,
  tipoForm,
  handleBlur,
  empresasSIIGenerador,
  isLoadingEmpresasSIIGenerador,
  cargaEmpresasSIIGenerador
}) => {
  const [razonSocialEmpresaGenerador, setRazonSocialEmpresaGenerador] =
    useState(values.generadorRazonSocial || '');

  //Si el formulario es un ingreso y no se ha ingresado ningun dato previamente de declaración mostraré la opción
  //de carga de un nuevo generador.
  const [isNuevo, setIsNuevo] = useState(
    tipoForm === DICTONARY.TIPO_FORMULARIO.ingresar &&
      !(
        values.rutEmpresaGenerador ||
        values.nombreEmpresaGenerador ||
        values.regionEmpresaGenerador ||
        values.comunaEmpresaGenerador ||
        values.nombreViaEmpresaGenerador
      )
      ? null
      : false
  );

  const [IsLoadingEmpresaGeneradora, setIsLoadingEmpresaGeneradora] =
    useState(false);

  useEffect(() => {
    if (!empresasSIIGenerador) return;
    if (empresasSIIGenerador.razonSocial) {
      setFieldValue('generadorRazonSocial', empresasSIIGenerador.razonSocial);
      setRazonSocialEmpresaGenerador(empresasSIIGenerador.razonSocial);
      if (touched.rutEmpresaGenerador) {
        setFieldValue(
          'nombreEmpresaGenerador',
          empresasSIIGenerador.razonSocial
        );
      }
    } else {
      setRazonSocialEmpresaGenerador('Sin información');
    }
  }, [empresasSIIGenerador, isLoadingEmpresasSIIGenerador]);

  // Carga RUT al ingresar al formulario
  useMemo(() => {
    if (values.rutEmpresaGenerador) {
      cargaEmpresasSIIGenerador(values.rutEmpresaGenerador);
    }
  }, [cargaEmpresasSIIGenerador, values.rutEmpresaGenerador]);

  useEffect(() => {
    const fecthEmpresaGeneradora = async codigoEmpresaGeneradora => {
      const empresaGeneradoraCargada = await obtenerEmpresaGeneradora({
        codigoEmpresaGeneradora: codigoEmpresaGeneradora,
        setIsLoading: setIsLoadingEmpresaGeneradora
      });
      if (empresaGeneradoraCargada) {
        if (isValorValido(empresaGeneradoraCargada.nombreEmpresa)) {
          setFieldValue(
            'nombreEmpresaGenerador',
            empresaGeneradoraCargada.nombreEmpresa
          );
        }
        if (isValorValido(empresaGeneradoraCargada.rut)) {
          setFieldValue('rutEmpresaGenerador', empresaGeneradoraCargada.rut);
        }

        if (isValorValido(empresaGeneradoraCargada.direccion)) {
          setFieldValue(
            'regionEmpresaGenerador',
            empresaGeneradoraCargada.direccion?.comuna?.region
          );
          setFieldValue(
            'comunaEmpresaGenerador',
            empresaGeneradoraCargada.direccion?.comuna
          );
          setFieldValue(
            'nombreViaEmpresaGenerador',
            empresaGeneradoraCargada.direccion?.nombreVia
          );
          setFieldValue(
            'latitudDireccionEmpresaGenerador',
            empresaGeneradoraCargada.direccion?.latitudDireccion
          );
          setFieldValue(
            'longitudDireccionEmpresaGenerador',
            empresaGeneradoraCargada.direccion?.longitudDireccion
          );
        }
      }
      setIsNuevo(false);
    };

    if (values.generadorPreCargado) {
      fecthEmpresaGeneradora(values.generadorPreCargado.value);
    }
  }, [values.generadorPreCargado]);

  const limpiarCampos = () => {
    setFieldValue('nombreEmpresaGenerador', null);
    setFieldValue('rutEmpresaGenerador', null);
    setFieldValue('regionEmpresaGenerador', null);
    setFieldValue('comunaEmpresaGenerador', null);
    setFieldValue('nombreViaEmpresaGenerador', null);
    setFieldValue('latitudDireccionEmpresaGenerador', null);
    setFieldValue('longitudDireccionEmpresaGenerador', null);
    setFieldValue('generadorRazonSocial', null);
    setRazonSocialEmpresaGenerador(null);
  };

  if (isNuevo === null) {
    return (
      <div className="col-span-12">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-12">
            <Heading type="h4" className="mb-0">
              Generador
            </Heading>
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-4">
            <AutoCompleteAsyncController
              name="generadorPreCargado"
              accesor="label"
              required
              fullWidth
              disabled={isSubmitting}
              label={
                <LabelComponent>
                  Selecciona un generador ya ingresado
                </LabelComponent>
              }
              setFieldValue={data => setFieldValue('generadorPreCargado', data)}
              setFieldTouched={() => setFieldTouched('generadorPreCargado')}
              isSubmitting={isSubmitting}
              value={values.generadorPreCargado}
              fetchCall={controllerValues =>
                obtenerEmpresasGeneradoras({
                  setLoadingEmpresas: controllerValues.setIsLoading,
                  setEmpresas: controllerValues.setData,
                  paginationEmpresas: controllerValues.paginationEmpresas,
                  nombreEmpresa: controllerValues.inputValue,
                  setRowCountEmpresas: controllerValues.setRowCountEmpresas
                })
              }
            />
          </div>
          <div className="hidden md:block col-span-1">
            <div className="flex h-full justify-center items-center relative">
              <div className="border-l absolute w-0.5 h-full z-0"></div>
              <span className="bg-white p-2 relative z-10">O</span>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 xl:col-span-3">
            <div>
              <LabelComponent>Añadir un nuevo generador</LabelComponent>
            </div>
            <ButtonComponent onClick={() => setIsNuevo(true)}>
              <Add className="mr-2" /> Nuevo generador
            </ButtonComponent>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-12">
      <div className="grid grid-cols-12 gap-4">
        {isNuevo ? (
          <div className="col-span-12 md:col-span-12 flex items-center gap-4">
            <Heading type="h4" className="mb-0">
              Añadir un nuevo generador
            </Heading>
            <ButtonComponent
              type="secondary"
              onClick={() => {
                setIsNuevo(null);
                limpiarCampos();
              }}
            >
              Cancelar
            </ButtonComponent>
          </div>
        ) : (
          <div className="col-span-12 md:col-span-12 flex items-center gap-4">
            <Heading type="h4" className="mb-0">
              {values.nombreEmpresaGenerador
                ? values.nombreEmpresaGenerador
                : 'Añadir un nuevo receptor'}
            </Heading>
            <ButtonComponent
              type="secondary"
              onClick={() => {
                setIsNuevo(null);
                limpiarCampos();
              }}
            >
              Cancelar
            </ButtonComponent>
          </div>
        )}

        <div className="col-span-12 xs:col-span-12">
          <div className="grid grid-cols-12 gap-4">
            {/* Rut Empresa */}
            <div className="col-span-12 md:col-span-4">
              <InputComponent
                autoComplete="off"
                disabled={isSubmitting}
                fullWidth
                name={'rutEmpresaGenerador'}
                value={values.rutEmpresaGenerador || ''}
                onChange={event => {
                  const { value } = event.target;
                  if (
                    value.length >= 9 &&
                    value.charAt(value.length - 2) === '-'
                  ) {
                    setFieldValue('rutEmpresaGenerador', value.trim());
                    cargaEmpresasSIIGenerador(value);
                  } else {
                    setFieldValue('rutEmpresaGenerador', value.trim());
                    setRazonSocialEmpresaGenerador('Sin información');
                  }
                }}
                onBlur={() => {
                  setFieldTouched('rutEmpresaGenerador');
                }}
                outerInputProps={{
                  inputProps: {
                    maxLength: 12,
                    onKeyPress: handleKeyPress
                  }
                }}
                onPaste={handlePaste}
                touched={touched.rutEmpresaGenerador}
                placeholder="Ej: 12345678-9"
                estado={
                  errors.rutEmpresaGenerador
                    ? { tipo: 'error', mensaje: errors.rutEmpresaGenerador }
                    : null
                }
                label={<LabelComponent>Rut </LabelComponent>}
              />
            </div>
            {/* Razon Social */}
            {razonSocialEmpresaGenerador && (
              <div className="col-span-12 md:col-span-4">
                <LabelComponent
                  tooltipText={
                    <>
                      <b>La razón social</b> ha sido{' '}
                      <b>obtenida de forma automática</b> con la información
                      entregada por el{' '}
                      <b>Servicio de Impuestos Internos (SII).</b>
                    </>
                  }
                >
                  Razón social
                </LabelComponent>
                <div className="rounded bg-neutral-90 p-2.5 relative">
                  <Text className="min-h-[1.25rem]">
                    {empresasSIIGenerador &&
                    empresasSIIGenerador.razonSocial &&
                    !isLoadingEmpresasSIIGenerador
                      ? razonSocialEmpresaGenerador
                      : !touched.rutEmpresaGenerador &&
                        !errors.rutEmpresaGenerador
                      ? ''
                      : 'Sin información'}
                    {isLoadingEmpresasSIIGenerador &&
                      touched.rutEmpresaGenerador &&
                      !errors.rutEmpresaGenerador && (
                        <CircularProgress
                          color="inherit"
                          className="opacity-40 !w-5 !h-5 absolute right-2.5 top-2.5"
                        />
                      )}
                  </Text>
                </div>
              </div>
            )}
            {/* Nombre */}
            <div className="col-span-12 md:col-span-4">
              <InputComponent
                autoComplete="off"
                disabled={isSubmitting}
                fullWidth
                name={'nombreEmpresaGenerador'}
                value={values.nombreEmpresaGenerador || ''}
                onChange={handleChange}
                onBlur={() => {
                  setFieldTouched('nombreEmpresaGenerador');
                }}
                touched={touched.nombreEmpresaGenerador}
                estado={
                  errors.nombreEmpresaGenerador
                    ? { tipo: 'error', mensaje: errors.nombreEmpresaGenerador }
                    : null
                }
                label={
                  <LabelComponent
                    tooltipText={
                      <>
                        <b>Nombre de fantasía</b> que sirve para{' '}
                        <b>agrupar los residuos declarados</b> de esta empresa.
                        Si se obtuvo <b>el dato de razón social</b> este{' '}
                        <b>será sugerido como nombre de empresa</b>. Sin embargo
                        puede <b>editar el campo</b> e indicar otro nombre para
                        la empresa.
                      </>
                    }
                  >
                    Nombre empresa{' '}
                  </LabelComponent>
                }
              />
            </div>
          </div>
          {console.log('values', values)}
          {/* Refactoring - direccion y mapa */}
          <SelectorDireccionContainer
            values={values}
            errors={errors}
            touched={touched}
            onChange={handleChange}
            handleBlur={handleBlur}
            setFieldTouched={setFieldTouched}
            setFieldValue={setFieldValue}
            isSubmitting={isSubmitting}
            esOpcionalTitulo={true}
            nameRegion="regionEmpresaGenerador"
            nameComuna="comunaEmpresaGenerador"
            nameNombreVia="nombreViaEmpresaGenerador"
            nameLatitud="latitudDireccionEmpresaGenerador"
            nameLongitud="longitudDireccionEmpresaGenerador"
            subtitle=""
            className="grid grid-cols-12 gap-4 mt-8"
          />
        </div>
      </div>
    </div>
  );
};

export default Generador;
